/**
 * Collapsible filter panel for mobile. Shows a "Filters" button that
 * expands to reveal filter controls. On desktop, children render inline.
 */

import { useState } from 'react'
import { Filter, ChevronDown, X } from 'lucide-react'
import { useIsMobile } from '../../hooks/useIsMobile'

interface MobileFilterPanelProps {
  children: React.ReactNode
  /** Number of active filters (shown as badge) */
  activeCount?: number
  className?: string
}

export default function MobileFilterPanel({ children, activeCount = 0, className = '' }: MobileFilterPanelProps) {
  const isMobile = useIsMobile()
  const [open, setOpen] = useState(false)

  // Desktop: render children inline
  if (!isMobile) {
    return <div className={`flex flex-wrap items-end gap-2 sm:gap-3 ${className}`}>{children}</div>
  }

  // Mobile: collapsible panel
  return (
    <div className={className}>
      <button
        onClick={() => setOpen(!open)}
        className="inline-flex items-center gap-2 px-3 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 active:bg-gray-100 min-h-[44px]"
      >
        <Filter size={16} />
        <span>Filters</span>
        {activeCount > 0 && (
          <span className="inline-flex items-center justify-center w-5 h-5 text-[10px] font-bold text-white bg-indigo-600 rounded-full">
            {activeCount}
          </span>
        )}
        <ChevronDown size={14} className={`text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="mt-2 p-3 bg-white border border-gray-200 rounded-xl shadow-sm space-y-3">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Filters</span>
            <button
              onClick={() => setOpen(false)}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg min-h-[44px] min-w-[44px] flex items-center justify-center"
              aria-label="Close filters"
            >
              <X size={16} />
            </button>
          </div>
          <div className="grid grid-cols-1 gap-3">{children}</div>
        </div>
      )}
    </div>
  )
}
